import { isEmpty } from "lodash";
import {
	ContactsFormKeys,
	contactsFieldSchema,
	FAQFormKeys,
	faqsFieldSchema,
	featuresFieldSchema,
	functionalCategoriesFieldSchema,
	LegalFormKeys,
	LegalItemKeys,
	LinkedResourcesFormKeys,
	legalUrlFieldSchema,
	linkedResourcesFieldSchema,
	longDescriptionFieldSchema,
	nameFieldSchema,
	QuickStartFormKeys,
	quickStartInstructionsFieldSchema,
	SEOFormKeys,
	SupportFormKeys,
	SupportItemKeys,
	searchAliasesFieldSchema,
	shortDescriptionFieldSchema,
	supportDescriptionFieldSchema,
	supportEmailFieldSchema,
	supportPhoneFieldSchema,
	supportUrlFieldSchema,
} from "../../../constants/formValidationSchemas";
import type { ProductListing } from "../../../types/generated-graphql";
import type { ProductData, ReviewField } from "./types";

const hasValue = (val: unknown): boolean => {
	if (val === null || val === undefined) return false;
	if (typeof val === "string") return val.trim().length > 0;
	if (Array.isArray(val)) return val.length > 0;
	if (typeof val === "object") return !isEmpty(val);
	return true;
};

const isEqual = (a: unknown, b: unknown): boolean => {
	if (a === b) return true;
	if (typeof a === "string" && typeof b === "string") return a.trim() === b.trim();
	if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
	if (a && b && typeof a === "object" && typeof b === "object") return JSON.stringify(a) === JSON.stringify(b);
	return false;
};

export const buildReviewFields = (aiData: ProductData, existingProduct: ProductListing): ReviewField[] => {
	const fields: ReviewField[] = [];

	// Product name
	if (hasValue(aiData.name)) {
		fields.push({
			key: "name",
			label: "Product name",
			aiValue: aiData.name,
			existingValue: existingProduct.name || null,
			hasConflict: hasValue(existingProduct.name),
			status: "pending",
			inputType: "text",
			pyxisPath: "name",
			validationSchema: nameFieldSchema,
		});
	}

	// Brief description
	if (hasValue(aiData.descriptions?.short)) {
		fields.push({
			key: "descriptions.short",
			label: "Brief description",
			aiValue: aiData.descriptions!.short ?? "",
			existingValue: existingProduct.descriptions?.short ?? null,
			hasConflict: hasValue(existingProduct.descriptions?.short),
			status: "pending",
			inputType: "text",
			pyxisPath: "descriptions.short",
			validationSchema: shortDescriptionFieldSchema,
		});
	}

	// Long description
	if (hasValue(aiData.descriptions?.long)) {
		fields.push({
			key: "descriptions.long",
			label: "Long description",
			aiValue: aiData.descriptions!.long ?? "",
			existingValue: existingProduct.descriptions?.long ?? null,
			hasConflict: hasValue(existingProduct.descriptions?.long),
			status: "pending",
			inputType: "richtext",
			pyxisPath: "descriptions.long",
			validationSchema: longDescriptionFieldSchema,
		});
	}

	// Features (array) - max 6
	if (hasValue(aiData.features)) {
		fields.push({
			key: "features",
			label: "Features & Benefits",
			aiValue: aiData.features!.slice(0, 6),
			existingValue: existingProduct.features?.filter((f) => hasValue(f?.title) || hasValue(f?.description)) || null,
			hasConflict: hasValue(existingProduct.features?.filter((f) => hasValue(f?.title) || hasValue(f?.description))),
			status: "pending",
			inputType: "array",
			pyxisPath: "features",
			validationSchema: featuresFieldSchema,
		});
	}

	// Quick start instructions
	if (hasValue(aiData.quick_start_configuration?.instructions)) {
		fields.push({
			key: QuickStartFormKeys.quick_start_configuration,
			label: "Quick start instructions",
			aiValue: aiData.quick_start_configuration!.instructions ?? "",
			existingValue: existingProduct.quick_start_configuration?.instructions ?? null,
			hasConflict: hasValue(existingProduct.quick_start_configuration?.instructions),
			status: "pending",
			inputType: "richtext",
			pyxisPath: "quick_start_configuration.instructions",
			validationSchema: quickStartInstructionsFieldSchema,
		});
	}

	// Linked resources (array)
	if (hasValue(aiData.linked_resources)) {
		fields.push({
			key: LinkedResourcesFormKeys.linked_resources,
			label: "Linked Resources",
			aiValue: aiData.linked_resources!.slice(0, 8),
			existingValue: existingProduct.linked_resources?.filter((r) => hasValue(r?.title) || hasValue(r?.url)) || null,
			hasConflict: hasValue(existingProduct.linked_resources?.filter((r) => hasValue(r?.title) || hasValue(r?.url))),
			status: "pending",
			inputType: "array",
			pyxisPath: "linked_resources",
			validationSchema: linkedResourcesFieldSchema,
		});
	}

	// FAQs (array)
	if (hasValue(aiData.faqs)) {
		fields.push({
			key: FAQFormKeys.faqs,
			label: "FAQs",
			aiValue: aiData.faqs!.slice(0, 4),
			existingValue: existingProduct.faqs?.filter((f) => hasValue(f?.question) || hasValue(f?.answer)) || null,
			hasConflict: hasValue(existingProduct.faqs?.filter((f) => hasValue(f?.question) || hasValue(f?.answer))),
			status: "pending",
			inputType: "array",
			pyxisPath: "faqs",
			validationSchema: faqsFieldSchema,
		});
	}

	// Support URL
	if (hasValue(aiData.support?.url)) {
		fields.push({
			key: `${SupportFormKeys.support}.${SupportItemKeys.url}`,
			label: "Support website",
			aiValue: aiData.support!.url,
			existingValue: existingProduct.support?.url || null,
			hasConflict: hasValue(existingProduct.support?.url),
			status: "pending",
			inputType: "text",
			pyxisPath: "support.url",
			validationSchema: supportUrlFieldSchema,
		});
	}

	// Support description
	if (hasValue(aiData.support?.description)) {
		fields.push({
			key: `${SupportFormKeys.support}.${SupportItemKeys.description}`,
			label: "Support description",
			aiValue: aiData.support!.description ?? "",
			existingValue: existingProduct.support?.description ?? null,
			hasConflict: hasValue(existingProduct.support?.description),
			status: "pending",
			inputType: "richtext",
			pyxisPath: "support.description",
			validationSchema: supportDescriptionFieldSchema,
		});
	}

	// Support email
	if (hasValue(aiData.support?.email_address)) {
		fields.push({
			key: `${SupportFormKeys.support}.${SupportItemKeys.email_address}`,
			label: "Support email address",
			aiValue: aiData.support!.email_address ?? "",
			existingValue: existingProduct.support?.email_address ?? null,
			hasConflict: hasValue(existingProduct.support?.email_address),
			status: "pending",
			inputType: "text",
			pyxisPath: "support.email_address",
			validationSchema: supportEmailFieldSchema,
		});
	}

	// Support phone
	if (hasValue(aiData.support?.phone_number)) {
		fields.push({
			key: `${SupportFormKeys.support}.${SupportItemKeys.phone_number}`,
			label: "Support phone number",
			aiValue: aiData.support!.phone_number ?? "",
			existingValue: existingProduct.support?.phone_number ?? null,
			hasConflict: hasValue(existingProduct.support?.phone_number),
			status: "pending",
			inputType: "phone",
			pyxisPath: "support.phone_number",
			validationSchema: supportPhoneFieldSchema,
		});
	}

	// Legal - license agreement URL
	if (hasValue(aiData.legal?.license_agreement_url)) {
		fields.push({
			key: `${LegalFormKeys.legal}.${LegalItemKeys.license_agreement_url}`,
			label: "License agreement URL",
			aiValue: aiData.legal!.license_agreement_url ?? "",
			existingValue: existingProduct.legal?.license_agreement_url ?? null,
			hasConflict: hasValue(existingProduct.legal?.license_agreement_url),
			status: "pending",
			inputType: "text",
			pyxisPath: "legal.license_agreement_url",
			validationSchema: legalUrlFieldSchema,
		});
	}

	// Legal - privacy policy URL
	if (hasValue(aiData.legal?.privacy_policy_url)) {
		fields.push({
			key: `${LegalFormKeys.legal}.${LegalItemKeys.privacy_policy_url}`,
			label: "Privacy policy URL",
			aiValue: aiData.legal!.privacy_policy_url ?? "",
			existingValue: existingProduct.legal?.privacy_policy_url ?? null,
			hasConflict: hasValue(existingProduct.legal?.privacy_policy_url),
			status: "pending",
			inputType: "text",
			pyxisPath: "legal.privacy_policy_url",
			validationSchema: legalUrlFieldSchema,
		});
	}

	// Functional categories
	if (hasValue(aiData.functional_categories)) {
		fields.push({
			key: SEOFormKeys.functional_categories,
			label: "Product category",
			aiValue: aiData.functional_categories!.slice(0, 3),
			existingValue: existingProduct.functional_categories?.filter((c): c is string => c !== null) ?? null,
			hasConflict: hasValue(existingProduct.functional_categories),
			status: "pending",
			inputType: "array",
			pyxisPath: "functional_categories",
			validationSchema: functionalCategoriesFieldSchema,
		});
	}

	// Search aliases (array)
	if (hasValue(aiData.search_aliases)) {
		fields.push({
			key: SEOFormKeys.search_aliases,
			label: "Search aliases",
			aiValue: aiData.search_aliases!.slice(0, 3),
			existingValue: existingProduct.search_aliases?.filter((s) => hasValue(s?.key) || hasValue(s?.value)) || null,
			hasConflict: hasValue(existingProduct.search_aliases?.filter((s) => hasValue(s?.key) || hasValue(s?.value))),
			status: "pending",
			inputType: "array",
			pyxisPath: "search_aliases",
			validationSchema: searchAliasesFieldSchema,
		});
	}

	// Contacts (array)
	if (hasValue(aiData.contacts)) {
		fields.push({
			key: ContactsFormKeys.contacts,
			label: "Contacts",
			aiValue: aiData.contacts!.slice(0, 10),
			existingValue: existingProduct.contacts?.filter((c) => hasValue(c?.email_address)) || null,
			hasConflict: hasValue(existingProduct.contacts?.filter((c) => hasValue(c?.email_address))),
			status: "pending",
			inputType: "array",
			pyxisPath: "contacts",
			validationSchema: contactsFieldSchema,
		});
	}

	return fields.filter((field) => !isEqual(field.aiValue, field.existingValue));
};

/**
 * Build a pyxis patch payload from a field key path and value.
 * e.g. "descriptions.short" + "hello" → { descriptions: { short: "hello" } }
 * e.g. "features" + [...] → { features: [...] }
 */
export const buildPatchPayload = (pyxisPath: string, value: unknown): Record<string, unknown> => {
	const parts = pyxisPath.split(".");
	if (parts.length === 1) {
		return { [parts[0]]: value };
	}
	// Nested path: only send the specific field being updated within its parent
	const parentKey = parts[0];
	const childKey = parts[parts.length - 1];
	return {
		[parentKey]: {
			[childKey]: value,
		},
	};
};
